import { useEffect, useRef, useState } from 'react'
import YouTube, { YouTubeProps } from 'react-youtube'
import Fullscreen from '@mui/icons-material/Fullscreen'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Slider from '@mui/material/Slider'
import Typography from '@mui/material/Typography'
import { getIn, useFormikContext } from 'formik'
import isEmpty from 'lodash/isEmpty'
import { RequiredDot } from 'pages/Survey/components/RequiredDot'
import { MediaBasedQuestion } from 'pages/Survey/types'
import { fontFamilies } from 'theme/styles.utils'
import { useGetObjectTranslation } from 'utils/useGetObjectTranslation'

const isValidYouTubeUrl = (url: string): boolean => {
  const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(\S*)?$/
  return pattern.test(url)
}

const extractVideoId = (url: string): string | null => {
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/)
  return match ? match[1] : null
}

export const SurveyMedia = ({ question }: { question: MediaBasedQuestion }) => {
  const translatedTitle = useGetObjectTranslation(question.translation, 'title')
  const translatedUrl = useGetObjectTranslation(question.translation, 'mediaUrl')
  const videoId = extractVideoId(translatedUrl)
  const isValid = isValidYouTubeUrl(translatedUrl)
  const playerRef = useRef<any>(null)
  const iframeRef = useRef<HTMLDivElement>(null)
  const { setFieldValue, touched: touchedArray, errors } = useFormikContext()
  const { touched, error } = { touched: getIn(touchedArray, question.id), error: getIn(errors, question.id) }
  const errorTextField = !isEmpty(error) && touched
  const helperText = touched && error && error

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  const handleFullscreen = () => {
    const container = iframeRef.current
    if (container?.requestFullscreen) {
      container.requestFullscreen()
    } else if ((container as any)?.webkitRequestFullscreen) {
      ;(container as any).webkitRequestFullscreen()
    } else if ((container as any)?.mozRequestFullScreen) {
      ;(container as any).mozRequestFullScreen()
    } else if ((container as any)?.msRequestFullscreen) {
      ;(container as any).msRequestFullscreen()
    }
  }

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.warn('Nie udało się wyjść z fullscreen:', err)
      })
    }
  }

  const onPlayerReady = (event: any) => {
    playerRef.current = event.target
    setDuration(event.target.getDuration())
    setCurrentTime(event.target.getCurrentTime())
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const player = playerRef.current
      if (player && typeof player.getCurrentTime === 'function') {
        try {
          setCurrentTime(player.getCurrentTime())
          setDuration(player.getDuration())
        } catch (err) {
          console.warn('YouTube API error:', err)
        }
      }
    }, 500)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const pauseVideo = () => {
      if (isValid && playerRef.current?.pauseVideo) {
        playerRef.current.pauseVideo()
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) pauseVideo()
    }
    const handleWindowBlur = () => pauseVideo()

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [isValid])

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" fontFamily={fontFamilies.default} mb={2}>
        {translatedTitle}
        {question.isRequired ? <RequiredDot /> : ''}
      </Typography>
      {isValid && videoId && (
        <Box sx={{ width: 640, height: 360, backgroundColor: 'black' }}>
          <Box ref={iframeRef} position="relative" width="100%" height={'100%'}>
            <YouTube
              ref={playerRef}
              videoId={videoId}
              loading="eager"
              opts={
                {
                  width: isFullscreen ? windowSize.width : 640,
                  height: isFullscreen ? windowSize.height : 360,
                  playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, fs: 1 },
                } as YouTubeProps['opts']
              }
              onReady={onPlayerReady}
              onPlay={() => {
                handleFullscreen()
                setShowControls(true)
              }}
              onEnd={() => {
                exitFullscreen()
                setShowControls(false)
                setFieldValue(question.id, 'true')
              }}
            />
            {showControls && (
              <>
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  width="calc(100% - 64px)"
                  pl={2}
                  pr={4}
                  pb={0}
                  sx={{ background: isFullscreen ? 'rgba(0,0,0,0.6)' : 'transparent' }}
                >
                  <Slider
                    color="primary"
                    value={currentTime === duration ? 100 : duration > 0 ? (currentTime / duration) * 100 : 0}
                    disabled
                    sx={{
                      pointerEvents: 'none',
                      '.MuiSlider-thumb': { width: 8, height: 8 },
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="white">
                      {formatTime(currentTime)}
                    </Typography>
                    <Typography variant="caption" color="white">
                      {formatTime(duration)}
                    </Typography>
                  </Box>
                </Box>

                <Box position="absolute" bottom={12} right={8}>
                  <IconButton size="large" color="primary" onClick={isFullscreen ? exitFullscreen : handleFullscreen}>
                    <Fullscreen />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
      {errorTextField && (
        <Typography color="error" mt={1}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}
